import React, { createContext, useState, useCallback, ReactNode } from "react";
import api from "../../api/api";
import { ApiResponse, PaginationInfo } from "../../types/api";
import { ClientPayment } from "../../types/models";
import { ClientPaymentContextType } from "../../types/context";

export const ClientPaymentContext = createContext<ClientPaymentContextType | undefined>(undefined);

interface ClientPaymentProviderProps {
  children: ReactNode;
}

export const ClientPaymentProvider: React.FC<ClientPaymentProviderProps> = ({ children }) => {
  const [payments, setPayments] = useState<ClientPayment[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPayments = useCallback(async (
    filters?: { invoice_id?: number; project_id?: number; page?: number; per_page?: number }
  ): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      let url = '/client-payments';
      const params = new URLSearchParams();
      if (filters?.invoice_id) params.append('invoice_id', filters.invoice_id.toString());
      if (filters?.project_id) params.append('project_id', filters.project_id.toString());
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.per_page) params.append('per_page', filters.per_page.toString());
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await api.get(url);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let paymentsData: ClientPayment[] = [];
      let paginationData: PaginationInfo | null = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          // Nested structure: response.data.data.data
          paymentsData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          // Direct array: response.data.data
          paymentsData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          paymentsData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        paymentsData = responseData.data;
      } else if (Array.isArray(responseData)) {
        paymentsData = responseData;
      }
      
      setPayments(paymentsData as ClientPayment[]);
      setPagination(paginationData);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
      console.error("Failed to fetch client payments:", err);
      setPayments([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const createPayment = useCallback(async (paymentData: any): Promise<ClientPayment> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post<ApiResponse<ClientPayment>>('/client-payments', paymentData);
      const newPayment = response.data.data;
      setPayments((prev) => [...prev, newPayment]);
      return newPayment;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const recordInvoicePayment = useCallback(async (invoiceId: number, paymentData: any): Promise<ClientPayment> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post<ApiResponse<ClientPayment>>(`/invoices/${invoiceId}/payments`, paymentData);
      const newPayment = response.data.data;
      setPayments((prev) => [...prev, newPayment]);
      return newPayment;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updatePayment = useCallback(async (id: number, paymentData: any): Promise<ClientPayment> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.put<ApiResponse<ClientPayment>>(`/client-payments/${id}`, paymentData);
      const updatedPayment = response.data.data;
      setPayments((prev) =>
        prev.map((payment) => (payment.id === id ? updatedPayment : payment))
      );
      return updatedPayment;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deletePayment = useCallback(async (id: number): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/client-payments/${id}`);
      setPayments((prev) => prev.filter((payment) => payment.id !== id));
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const contextValue: ClientPaymentContextType = {
    payments,
    pagination,
    loading,
    error,
    fetchPayments,
    createPayment,
    recordInvoicePayment,
    updatePayment,
    deletePayment,
  };

  return (
    <ClientPaymentContext.Provider value={contextValue}>
      {children}
    </ClientPaymentContext.Provider>
  );
};

export default ClientPaymentProvider;

